import { lazy, ReactNode, Suspense, useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { useAuth } from './lib/platform';
import { useReferralCapture } from './lib/referral';
import { Loader } from './components/Loader';
import { LANDING_ROUTES, VariantCatchAll } from './LandingApp';
import TitledRoute from './components/TitledRoute';
import ParticleBackground from './components/ux/ParticleBackground';

// Lazy load the three separate apps for code splitting
const LandingApp = lazy(() => import('./LandingApp'));
const MainApp = lazy(() => import('./MainApp'));
const AdminApp = lazy(() => import('./AdminApp'));

// Lazy load legal pages
const Terms = lazy(() => import('./pages/static/Terms'));
const Privacy = lazy(() => import('./pages/static/Privacy'));
const CommunityGuidelines = lazy(() => import('./pages/static/CommunityGuidelines'));
const Locations = lazy(() => import('./pages/static/Locations'));
const Affiliate = lazy(() => import('./pages/static/Affiliate'));
const Help = lazy(() => import('./pages/static/Help'));
const Host = lazy(() => import('./pages/static/Host'));
const Venues = lazy(() => import('./pages/static/Venues'));

// Public event page (/events/:slug) — sharable, unauthenticated.
const EventPublic = lazy(() => import('./pages/EventPublic'));
const EventInterested = lazy(() => import('./pages/EventInterested'));
// Kiki detail page (/events/:slug/kiki/:requestId) — event + buddy request + chat.
const KikiPage = lazy(() => import('./pages/KikiPage'));
// Public events directory (/events) — grid of all public host events.
const EventsIndex = lazy(() => import('./pages/EventsIndex'));
// Public group profile (/g/:slug) — sharable, unauthenticated.
const GroupPublic = lazy(() => import('./pages/GroupPublic'));

// Temporary design lab for the profile editor (unauthenticated preview).
const ProfileLab = lazy(() => import('./pages/ProfileLab'));

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader />
    </div>
  );
}

function WrappedSignedOutPage(props: {children: ReactNode}) {
  return (
    <div>
      <ParticleBackground />
      {props.children}
    </div>
  )
}

// Signed-in users who land on the public events directory (/events) are sent to the
// authenticated shell. The redirect lives here rather than in EventsIndex
// because the in-app events page renders the same component — doing it in the
// page itself would loop.
function EventsIndexRoute(props: {isAppDomain: boolean}) {
  const { isLoaded, isSignedIn } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (!(isLoaded && isSignedIn)) return;
    const search = window.location.search;
    const appOrigin = 'https://app.outclose.com';
    if (props.isAppDomain) {
      // On the app subdomain the events index is at /events (no /app prefix).
      window.location.replace(`${appOrigin}/events${search}`);
    } else {
      navigate('/app/events' + search, { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate, props.isAppDomain]);

  // Once we know we're redirecting, don't flash the public page.
  if (isLoaded && isSignedIn) return null;

  return (
    <WrappedSignedOutPage>
      <EventsIndex />
    </WrappedSignedOutPage>
  );
}

function App() {
  const [location] = useLocation();
  const hostname = window.location.hostname;
  
  // Determine if we're on the app subdomain
  const isAppDomain = ['app.gayborhood.io', 'app.outclose.com'].includes(hostname);

  // Determine if we're on the help subdomain
  const isHelpDomain = ['help.outclose.com'].includes(hostname);
  
  // Capture referral code from URL if present
  useReferralCapture();

  // Help page manages its own title via i18n once translations load;
  // the TitledRoute title acts as an initial placeholder.
  const renderApp = () => {
    if (location.startsWith('/admin')) {
      return <AdminApp />;
    }

    // If we're on app.*, always render MainApp
    if (isAppDomain || location.startsWith('/app')) {
      // Pass '/' as base path when on app subdomain, '/app' otherwise
      const basePath = isAppDomain ? '/' : '/app';
      return <MainApp basePath={basePath} />;
    }

    // Help center root on the help.outclose.com subdomain.
    if (isHelpDomain) {
      return <Help />;
    }

    return (
      <Switch>
        <TitledRoute path="/terms" title="Terms of Service - Outclose">
          <Terms />
        </TitledRoute>
        <TitledRoute path="/privacy" title="Privacy Policy - Outclose">
          <Privacy />
        </TitledRoute>
        <TitledRoute path="/community-guidelines" title="Community Guidelines - Outclose">
          <CommunityGuidelines />
        </TitledRoute>
        <TitledRoute path="/locations" title="Locations - Outclose">
          <Locations />
        </TitledRoute>
        <TitledRoute path="/affiliate" title="Affiliate Program - Outclose">
          <Affiliate />
        </TitledRoute>
        <TitledRoute path="/help" title="Help Center - Outclose">
          <Help />
        </TitledRoute>
        <TitledRoute path="/venues" title="Host a group night - Outclose">
          <Venues />
        </TitledRoute>
        <TitledRoute path="/host" title="Host on Outclose">
          <Host />
        </TitledRoute>
        <TitledRoute path="/profile-lab" title="Profile Lab - Outclose">
          <ProfileLab />
        </TitledRoute>
        <Route path="/kiki/:id">
          <WrappedSignedOutPage>
            <KikiPage />
          </WrappedSignedOutPage>
        </Route>
        <Route path="/events/:slug/interested">
          <WrappedSignedOutPage>
            <EventInterested />
          </WrappedSignedOutPage>
        </Route>
        <Route path="/events/:slug">
          <WrappedSignedOutPage>
            <EventPublic />
          </WrappedSignedOutPage>
        </Route>
        <Route path="/events">
          <EventsIndexRoute isAppDomain={isAppDomain} />
        </Route>
        {/* Clubs (mock v2) — creator clubs; public like /groups. */}
        <Route path="/clubs/host">
          <MainApp basePath="" />
        </Route>
        <Route path="/clubs/:slug/discussion">
          <MainApp basePath="" />
        </Route>
        <Route path="/clubs/:slug">
          <MainApp basePath="" />
        </Route>
        <Route path="/clubs">
          <MainApp basePath="" />
        </Route>
        {/* Groups (mock) — public like /events; MainApp renders them without the auth wall. */}
        <Route path="/groups/notifications">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups/:slug/discussion">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups/:slug/chat">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups/:slug/co-host">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups/:slug/:eventId">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups/:slug">
          <MainApp basePath="" />
        </Route>
        <Route path="/groups">
          <MainApp basePath="" />
        </Route>
        {/* /g/new must precede /g/:slug — redirect into the authenticated app. */}
        <Route path="/g/new">
          <MainApp basePath="" />
        </Route>
        <Route path="/g/:slug/edit">
          <MainApp basePath="" />
        </Route>
        <Route path="/g/:slug">
          <GroupPublic />
        </Route>
        <Route>
          {LANDING_ROUTES[location] || location.startsWith('/tpl/') ? (
            <LandingApp />
          ) : /^\/[a-z0-9]{2,20}$/.test(location) ? (
            // Unknown short slug: try the landing-variant API (MCP-created
            // pages get clean paths with no deploy); 404s if no variant.
            <VariantCatchAll slug={location.slice(1)} />
          ) : (
            <div className="h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
                <p className="text-dark-muted">Page not found</p>
                <p>[{location}]</p>
              </div>
            </div>
          )}
        </Route>
      </Switch>
    );
  };

  return (
    <Suspense fallback={<LoadingScreen />}>
      {renderApp()}
    </Suspense>
  );
}

export default App;
